import { AnimatePresence, motion } from 'framer-motion';
import { X, UserPlus, Shield, Users, Briefcase, KeyRound, Info, Lock, Brain, Clock } from 'lucide-react';
import { formatLastAnalysisDate } from '../utils/dateFormatter';
import { useLexLastSync } from '../contexts/LexLastSyncContext';

interface PrismeUserCreationGuideModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function PrismeUserCreationGuideModal({ isOpen, onClose }: PrismeUserCreationGuideModalProps) {
  const { dates: lexDates } = useLexLastSync();
  const lastAnalysisDate = isOpen ? (lexDates['creer_utilisateur_prisme_last_ai_analysis'] ?? null) : null;

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 12 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-linear-to-br from-slate-950 via-slate-900 to-slate-950 rounded-2xl w-full max-w-5xl max-h-[92vh] overflow-hidden border border-slate-700/60 shadow-2xl shadow-black/40 flex flex-col">
              {/* Header */}
              <div className="sticky top-0 z-10 bg-linear-to-r from-emerald-600/25 via-cyan-600/20 to-indigo-600/25 border-b border-white/10 p-6">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-2xl bg-white/10 border border-white/10 flex items-center justify-center shadow-inner">
                      <UserPlus className="w-6 h-6 text-emerald-200" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-white">Création d’utilisateurs — PRISME</h2>
                      <p className="text-sm text-white/75">
                        Guide opérationnel pour <strong>PRISME MASTER</strong>, <strong>PRISME CLIENT</strong> et{' '}
                        <strong>PRISME EMPLOYÉ</strong>.
                      </p>
                      <div className="flex items-center gap-3 mt-2 flex-wrap">
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-purple-500/30 border border-purple-400/50 text-xs text-purple-200" title="Disponible dans AI Assistant">
                          <Brain className="w-3.5 h-3.5" />
                          AI
                        </span>
                        {lastAnalysisDate && (
                          <span className="text-white/70 text-xs flex items-center gap-1.5">
                            <Clock className="w-3.5 h-3.5" />
                            Dernière synchro LEX: {formatLastAnalysisDate(lastAnalysisDate)}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={onClose}
                    className="p-2 rounded-xl hover:bg-white/10 transition-colors text-white/80 hover:text-white"
                    aria-label="Fermer"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6 md:p-8 bg-linear-to-b from-slate-950 to-slate-900">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* MASTER */}
                  <div className="rounded-2xl border border-emerald-500/25 bg-emerald-500/10 p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <Shield className="w-5 h-5 text-emerald-300" />
                      <h3 className="text-base font-bold text-white">PRISME MASTER</h3>
                    </div>
                    <p className="text-sm text-white/80">
                      Création via <strong>Administration PRISME BZ</strong> (Service &amp; Direction TI).
                    </p>
                  </div>

                  {/* CLIENT */}
                  <div className="rounded-2xl border border-cyan-500/25 bg-cyan-500/10 p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <Briefcase className="w-5 h-5 text-cyan-300" />
                      <h3 className="text-base font-bold text-white">PRISME CLIENT</h3>
                    </div>
                    <p className="text-sm text-white/80">
                      Même point d’entrée que Master: <strong>Administration PRISME BZ</strong>.
                    </p>
                  </div>

                  {/* EMPLOYÉ */}
                  <div className="rounded-2xl border border-indigo-500/25 bg-indigo-500/10 p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <Users className="w-5 h-5 text-indigo-300" />
                      <h3 className="text-base font-bold text-white">PRISME EMPLOYÉ</h3>
                    </div>
                    <p className="text-sm text-white/80">
                      Activation via <strong>TactiCE RH</strong> (accès restreint).
                    </p>
                  </div>
                </div>

                <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="rounded-2xl border border-white/10 bg-white/5 p-6">
                    <h4 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
                      <Shield className="w-5 h-5 text-emerald-300" />
                      PRISME MASTER / PRISME CLIENT — création d’un utilisateur
                    </h4>

                    <ol className="space-y-2 text-sm text-white/85 list-decimal pl-5">
                      <li>
                        Aller dans <strong>Service &amp; Direction TI</strong>.
                      </li>
                      <li>
                        Ouvrir la card <strong>Administration PRISME BZ</strong>.
                      </li>
                      <li>
                        Aller dans l’onglet <strong>Parties prenantes</strong>.
                      </li>
                      <li>
                        Créer une nouvelle partie prenante (ou modifier une existante).
                      </li>
                      <li>
                        Cocher <code>is_user</code> et saisir un <strong>mot de passe</strong> (requis lors de l’activation).
                      </li>
                      <li>
                        Valider/compléter l’association du contact (client associé) si applicable, puis sauvegarder.
                      </li>
                    </ol>

                    <div className="mt-4 rounded-xl border border-amber-500/25 bg-amber-500/10 p-4">
                      <div className="flex items-start gap-2">
                        <Info className="w-5 h-5 text-amber-300 mt-0.5" />
                        <p className="text-sm text-white/85">
                          <strong>Droits d’accès:</strong> après cette étape, les accès sont gérés <strong>dans le code</strong> (hardcodé via
                          Cursor). <strong>Il n’existe pas de table</strong> dédiée pour gérer les accès Master/Client.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-2xl border border-white/10 bg-white/5 p-6">
                    <h4 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
                      <Users className="w-5 h-5 text-indigo-300" />
                      PRISME EMPLOYÉ — activation d’un employé
                    </h4>

                    <ol className="space-y-2 text-sm text-white/85 list-decimal pl-5">
                      <li>
                        Passer par <strong>TactiCE RH</strong>.
                      </li>
                      <li>
                        Sur la fiche employé, cocher <code>ISUSERPRISMEEMPLOYE</code> et saisir un <strong>mot de passe</strong>.
                      </li>
                    </ol>

                    <div className="mt-4 rounded-xl border border-white/10 bg-black/20 p-4">
                      <p className="text-sm text-white/85 font-semibold mb-2">Accès autorisés (TactiCE RH)</p>
                      <ul className="text-sm text-white/80 space-y-1">
                        <li>
                          <strong>Peuvent activer</strong> <code>ISUSERPRISMEEMPLOYE</code>: Mélissa Pelletier, Emilie Quintal, Dany Bourassa,
                          Eric Durand.
                        </li>
                        <li>
                          <strong>Ont aussi</strong> <code>ISADMINPRISMEEMPLOYE</code> (accès à tous les PRISME employé): Dany Bourassa,
                          Jean-Pierre Leclerc, Eric Durand.
                        </li>
                      </ul>
                    </div>

                    <div className="mt-4 rounded-xl border border-slate-500/30 bg-slate-500/10 p-4">
                      <div className="flex items-start gap-2">
                        <Lock className="w-5 h-5 text-slate-200 mt-0.5" />
                        <p className="text-sm text-white/85">
                          Le reste des accès est <strong>hardcodé</strong> dans PRISME Employé: l’employé + ses gestionnaires, et toujours les
                          admins PRISME Employé.
                        </p>
                      </div>
                    </div>

                    <div className="mt-4 rounded-xl border border-cyan-500/25 bg-cyan-500/10 p-4">
                      <div className="flex items-start gap-2">
                        <KeyRound className="w-5 h-5 text-cyan-300 mt-0.5" />
                        <p className="text-sm text-white/85">
                          Si un employé a besoin de permissions supplémentaires, ça se fait via une modification code (Cursor), pas via une table
                          d’autorisations.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-6 rounded-2xl border border-white/10 bg-white/5 p-6">
                  <h4 className="text-lg font-bold text-white mb-2">Checklist express</h4>
                  <ul className="space-y-2 text-sm text-white/85">
                    <li>
                      - <strong>Master/Client</strong>: Administration PRISME BZ → Parties prenantes → <code>is_user</code> + mot de passe →
                      associer le contact → sauvegarder → droits hardcodés.
                    </li>
                    <li>
                      - <strong>Employé</strong>: TactiCE RH → <code>ISUSERPRISMEEMPLOYE</code> + mot de passe → (optionnel){' '}
                      <code>ISADMINPRISMEEMPLOYE</code> selon les admins → droits hardcodés par employé.
                    </li>
                  </ul>
                </div>
              </div>

              {/* Footer */}
              <div className="p-5 border-t border-white/10 bg-slate-950/80 flex items-center justify-end">
                <button
                  onClick={onClose}
                  className="px-6 py-2.5 rounded-xl bg-white text-slate-900 font-semibold hover:bg-slate-100 transition-colors"
                >
                  Fermer
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

